<template>
  <div class="journey-chat">
    <div class="journey-chat-messages" ref="messagesContainer">
      <div v-for="(msg, i) in messages" :key="i" :class="['journey-chat-message', msg.role]">
        <div class="journey-chat-bubble">
          <span>{{ msg.text }}</span>
          <span v-if="msg.hasJson" class="journey-chat-json-badge">Journey updated</span>
          <span v-if="msg.debug" class="journey-chat-debug">{{ msg.debug.model }} · {{ msg.debug.totalTokens }} tokens</span>
        </div>
      </div>
      <div v-if="loading" class="journey-chat-message system">
        <div class="journey-chat-bubble journey-chat-bubble--loading">
          <span class="dot" /><span class="dot" /><span class="dot" />
        </div>
      </div>
      <div v-if="!messages.length && !loading" class="journey-chat-empty">
        Describe the journey you want to build and I'll generate it for you.
      </div>
    </div>
    <div v-if="messages.length" class="journey-chat-toolbar">
      <button class="journey-chat-clear" @click="clear">Clear</button>
    </div>
    <div class="journey-chat-input-bar">
      <input
        v-model="inputText"
        class="journey-chat-input"
        placeholder="e.g. Build a 3-stage diabetes screening journey..."
        :disabled="loading"
        @keydown.enter="send"
      />
      <button class="journey-chat-send" :disabled="loading" @click="send">
        <svg viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
          <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import api from '../../../utils/api'

export default {
  name: 'JourneyChat',
  props: {
    currentJourneyData: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      messages: [],
      inputText: '',
      loading: false
    }
  },
  methods: {
    async send () {
      const text = this.inputText.trim()
      if (!text || this.loading) return

      this.inputText = ''
      this.messages.push({ role: 'user', text })
      this.loading = true
      this.scrollToBottom()

      try {
        const history = this.messages.slice(0, -1).map(m => ({ role: m.role, text: m.text }))
        const hasJourney = this.currentJourneyData && this.currentJourneyData.config && this.currentJourneyData.config.stages
        const response = await api.buildRequest('journey-builder-chat', 'POST', {
          message: text,
          history,
          ...(hasJourney && { journeyJson: this.currentJourneyData })
        })
        const { message, journeyJson, debug } = response.data

        this.messages.push({ role: 'system', text: message, hasJson: !!journeyJson, debug })

        if (journeyJson) {
          this.$emit('journey-updated', journeyJson)
        }
      } catch (err) {
        this.messages.push({ role: 'system', text: 'Something went wrong. Please try again.' })
      } finally {
        this.loading = false
        this.scrollToBottom()
      }
    },
    clear () {
      this.messages = []
      this.$emit('journey-cleared')
    },
    scrollToBottom () {
      this.$nextTick(() => {
        const el = this.$refs.messagesContainer
        if (el) el.scrollTop = el.scrollHeight
      })
    }
  }
}
</script>

<style scoped>
.journey-chat {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
}
.journey-chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.journey-chat-empty {
  color: #a0aec0;
  font-size: 13px;
  text-align: center;
  margin-top: 40px;
  line-height: 1.6;
}
.journey-chat-message {
  display: flex;
}
.journey-chat-message.user {
  justify-content: flex-end;
}
.journey-chat-message.system {
  justify-content: flex-start;
}
.journey-chat-bubble {
  max-width: 80%;
  padding: 8px 12px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.5;
  background: #e2e8f0;
  color: #2d3748;
  display: flex;
  flex-direction: column;
  gap: 6px;
  white-space: pre-wrap;
}
.journey-chat-message.user .journey-chat-bubble {
  background: #0D9488;
  color: #fff;
  border-bottom-right-radius: 4px;
}
.journey-chat-message.system .journey-chat-bubble {
  border-bottom-left-radius: 4px;
}
.journey-chat-json-badge {
  display: inline-block;
  font-size: 11px;
  background: #c6f6d5;
  color: #276749;
  border-radius: 4px;
  padding: 2px 6px;
  align-self: flex-start;
}
.journey-chat-debug {
  display: inline-block;
  font-size: 10px;
  color: #a0aec0;
  align-self: flex-start;
  font-family: monospace;
}
.journey-chat-bubble--loading {
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
  padding: 12px 16px;
}
.dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #a0aec0;
  animation: bounce 1.2s infinite ease-in-out;
}
.dot:nth-child(2) { animation-delay: 0.2s; }
.dot:nth-child(3) { animation-delay: 0.4s; }
@keyframes bounce {
  0%, 80%, 100% { transform: scale(0.7); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
}
.journey-chat-toolbar {
  display: flex;
  justify-content: flex-end;
  padding: 4px 12px 0;
  flex-shrink: 0;
}
.journey-chat-clear {
  background: none;
  border: none;
  font-size: 12px;
  color: #a0aec0;
  cursor: pointer;
  padding: 2px 6px;
}
.journey-chat-clear:hover {
  color: #e53e3e;
}
.journey-chat-input-bar {
  display: flex;
  align-items: center;
  border-top: 1px solid #e2e8f0;
  padding: 10px 12px;
  gap: 8px;
  flex-shrink: 0;
}
.journey-chat-input {
  flex: 1;
  border: 1px solid #cbd5e0;
  border-radius: 20px;
  padding: 7px 14px;
  font-size: 13px;
  outline: none;
}
.journey-chat-input:focus {
  border-color: #0D9488;
}
.journey-chat-input:disabled {
  background: #f7fafc;
}
.journey-chat-send {
  background: #0D9488;
  color: #fff;
  border: none;
  border-radius: 50%;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
}
.journey-chat-send:hover:not(:disabled) {
  background: #0B7C72;
}
.journey-chat-send:disabled {
  background: #a0aec0;
  cursor: not-allowed;
}
</style>



// WEBPACK FOOTER //
// JourneyChat.vue?1eb8887e